import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Divider,
  Flex,
  Input,
  message,
  Select,
  Spin,
  Typography,
} from 'antd';
import { CopyOutlinedRect } from '@/assets/icons/icons';
import { CustomModalWithoutButton, CustomButton } from '../../components';
import { GetShipmentBookingById } from '../../apis/shipment';
import { parseShipmentBookingFromGetResponse } from './quickstatBookingGetResponse';
import {
  bookingRowToEmailSource,
  type QuickstatBookingEmailSource,
} from './quickstatBookingEmailFormat';
import {
  buildQuickstatBookingEmailBody,
  buildQuickstatBookingEmailBodyHtml,
  buildQuickstatBookingEmailSubject,
  QUICKSTAT_EMAIL_TEMPLATE_OPTIONS,
  type QuickstatEmailTemplateId,
} from './quickstatBookingEmailTemplates';

import './QuickstatBookingCompletedModal.css';

const { Text } = Typography;

const POLL_MAX_MS = 180_000;
const POLL_INTERVAL_MS = 2_000;

/** Same asset as shipment drawer serial copy (`ShipmentDetailProductsTable`). */
const shipmentDrawerCopyIcon = (
  <CopyOutlinedRect size={14} color='currentColor' />
);

/** Compact tertiary-style copy actions (matches drawer-scale controls). */
const compactCopyButtonProps = {
  height: 28 as const,
  fontSize: '12px',
  fontWeight: '600' as const,
  gap: 6 as const,
  padding: '0 10px',
  bgColor: 'var(--surface-container)',
  color: 'var(--text-primary)',
  border: '1px solid #d9d9d9',
  borderRadius: '8px',
  boxShadow: 'none',
};

function bookingEmailSourceEqual(
  a: QuickstatBookingEmailSource | null,
  b: QuickstatBookingEmailSource | null
): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export interface QuickstatBookingCompletedModalProps {
  open: boolean;
  onClose: () => void;
  bookingId: string | null;
  token: string;
  companyName: string;
}

function fallbackCopyToClipboard(text: string, successMessage: string): void {
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  try {
    document.execCommand('copy');
    message.success(successMessage);
  } catch {
    message.error('Copy failed. Please copy manually.');
  }
  document.body.removeChild(textarea);
}

function copyText(text: string, successMessage: string): void {
  if (navigator.clipboard && window.isSecureContext) {
    void navigator.clipboard.writeText(text).then(
      () => message.success(successMessage),
      () => fallbackCopyToClipboard(text, successMessage)
    );
  } else {
    fallbackCopyToClipboard(text, successMessage);
  }
}

/** Outlook and many mail clients consume `text/html` from the clipboard; plain is fallback. */
async function copyHtmlAndPlainToClipboard(
  html: string,
  plain: string,
  successMessage: string
): Promise<void> {
  try {
    if (
      typeof ClipboardItem !== 'undefined' &&
      typeof navigator.clipboard?.write === 'function'
    ) {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([plain], { type: 'text/plain' }),
        }),
      ]);
      message.success(successMessage);
      return;
    }
  } catch {
    // e.g. browser without multi-MIME ClipboardItem — use plain only
  }
  copyText(plain, successMessage);
}

const QuickstatBookingCompletedModal: React.FC<
  QuickstatBookingCompletedModalProps
> = ({ open, onClose, bookingId, token, companyName }) => {
  const [portalOrder, setPortalOrder] = useState<string | null>(null);
  const [bookingSource, setBookingSource] =
    useState<QuickstatBookingEmailSource | null>(null);
  const [pollTimedOut, setPollTimedOut] = useState(false);
  const [fetchFailed, setFetchFailed] = useState(false);
  /** Set when API reports terminal automation status (stop polling). */
  const [bookingTerminalStatus, setBookingTerminalStatus] = useState<
    'failed' | 'cancelled' | null
  >(null);
  /**
   * Booking is already `submitted` but has no portal order — normal for local
   * rows / tests where automation never ran; do not spin for 3 minutes.
   */
  const [submittedWithoutPortalOrder, setSubmittedWithoutPortalOrder] =
    useState(false);
  const [waitingForOrderNumber, setWaitingForOrderNumber] = useState(false);
  const [templateId, setTemplateId] =
    useState<QuickstatEmailTemplateId>('credo_obc');

  // Only the Done button closes this modal — backdrop, Escape, and X are all disabled.
  const setModalOpenBridge = useCallback((_next: boolean) => {}, []);

  useEffect(() => {
    if (!open || !bookingId || !token) return;
    const currentBookingId = bookingId;

    setPortalOrder(null);
    setBookingSource(null);
    setPollTimedOut(false);
    setFetchFailed(false);
    setBookingTerminalStatus(null);
    setSubmittedWithoutPortalOrder(false);
    setWaitingForOrderNumber(false);
    setTemplateId('credo_obc');

    let cancelled = false;
    let timer: number | null = null;
    const startedAt = Date.now();

    function clearPollTimer(): void {
      if (timer != null) {
        window.clearTimeout(timer);
        timer = null;
      }
    }

    function scheduleNext(): void {
      if (cancelled) return;
      clearPollTimer();
      timer = window.setTimeout(() => {
        tick().catch((): void => undefined);
      }, POLL_INTERVAL_MS);
    }

    async function tick(): Promise<void> {
      if (cancelled) {
        setWaitingForOrderNumber(false);
        return;
      }
      setWaitingForOrderNumber(true);
      try {
        const res = await GetShipmentBookingById(token, currentBookingId);
        if (cancelled) {
          setWaitingForOrderNumber(false);
          return;
        }
        setFetchFailed(false);
        const {
          row,
          portalOrder: po,
          bookingStatus,
        } = parseShipmentBookingFromGetResponse(res);
        const src = bookingRowToEmailSource(row);
        if (src) {
          setBookingSource((prev) =>
            bookingEmailSourceEqual(prev, src) ? prev : src
          );
        }

        if (bookingStatus === 'failed') {
          setBookingTerminalStatus('failed');
          setWaitingForOrderNumber(false);
          clearPollTimer();
          if (po) setPortalOrder(po);
          return;
        }
        if (bookingStatus === 'cancelled') {
          setBookingTerminalStatus('cancelled');
          setWaitingForOrderNumber(false);
          clearPollTimer();
          return;
        }

        if (bookingStatus === 'submitted' && !po) {
          setSubmittedWithoutPortalOrder(true);
          setWaitingForOrderNumber(false);
          clearPollTimer();
          return;
        }

        if (po) {
          setPortalOrder(po);
          setWaitingForOrderNumber(false);
          clearPollTimer();
          return;
        }
      } catch {
        if (!cancelled) setFetchFailed(true);
      }

      if (cancelled) {
        setWaitingForOrderNumber(false);
        return;
      }
      if (Date.now() - startedAt >= POLL_MAX_MS) {
        setPollTimedOut(true);
        setWaitingForOrderNumber(false);
        return;
      }
      scheduleNext();
    }

    tick().catch((): void => undefined);

    return () => {
      cancelled = true;
      clearPollTimer();
    };
  }, [open, bookingId, token]);

  const subject = useMemo(
    () =>
      buildQuickstatBookingEmailSubject(
        companyName,
        portalOrder ?? '',
        bookingSource ?? {},
        templateId
      ),
    [companyName, portalOrder, bookingSource, templateId]
  );

  const bodyPlain = useMemo(
    () =>
      buildQuickstatBookingEmailBody(
        templateId,
        portalOrder ?? '',
        bookingSource ?? {}
      ),
    [templateId, portalOrder, bookingSource]
  );

  const bodyHtml = useMemo(
    () =>
      buildQuickstatBookingEmailBodyHtml(
        templateId,
        portalOrder ?? '',
        bookingSource ?? {}
      ),
    [templateId, portalOrder, bookingSource]
  );

  const handleCopySubject = useCallback(() => {
    if (!portalOrder) return;
    copyText(subject, 'Subject copied to clipboard.');
  }, [portalOrder, subject]);

  const handleCopyBody = useCallback(() => {
    copyHtmlAndPlainToClipboard(
      bodyHtml,
      bodyPlain,
      'Email body copied to clipboard.'
    ).catch((): void => undefined);
  }, [bodyHtml, bodyPlain]);

  return (
    <CustomModalWithoutButton
      modalOpen={open}
      setModalOpen={setModalOpenBridge}
      modalTitle='Quickstat booking completed'
      lineHeight='28px'
      fontSize='20px'
      modalSize={600}
      zIndex={1200}
      maskClosable={false}
      closable={false}
      keyboard={false}
    >
      <Flex vertical gap={0} style={{ width: '100%' }}>
        <Typography.Paragraph style={{ marginBottom: 12, marginTop: 0 }}>
          Your AWB # is{' '}
          {portalOrder ? (
            <Text strong>{portalOrder}</Text>
          ) : bookingTerminalStatus === 'failed' ? (
            <Text type='secondary'>
              unavailable — automation failed (see below). You can still copy
              the email body and add the AWB manually if you obtain it
              elsewhere.
            </Text>
          ) : bookingTerminalStatus === 'cancelled' ? (
            <Text type='secondary'>
              not applicable — this booking was cancelled.
            </Text>
          ) : submittedWithoutPortalOrder ? (
            <Text type='secondary'>
              not stored on this booking yet (common locally if QuickOnline
              automation did not run). Use the email body below and add the AWB
              manually, or set <code>quickstat_portal_order_number</code> on the
              row.
            </Text>
          ) : pollTimedOut ? (
            <Text type='secondary'>
              not available yet — copy the email below and add the AWB when it
              appears in your booking record.
            </Text>
          ) : (
            <span>
              <Spin size='small' /> <Text type='secondary'>Loading…</Text>
            </span>
          )}
        </Typography.Paragraph>

        {fetchFailed ? (
          <Alert
            type='warning'
            showIcon
            message='Could not refresh booking details. Email text uses the last loaded data.'
            style={{ marginBottom: 12 }}
          />
        ) : null}

        {bookingTerminalStatus === 'failed' ? (
          <Alert
            type='error'
            showIcon
            message='Booking automation reported a failure. The QuickOnline order number may not appear; check the booking record or retry from Operations.'
            style={{ marginBottom: 12 }}
          />
        ) : null}

        {bookingTerminalStatus === 'cancelled' ? (
          <Alert
            type='warning'
            showIcon
            message='This booking was cancelled. Polling for the order number has stopped.'
            style={{ marginBottom: 12 }}
          />
        ) : null}

        {pollTimedOut && !portalOrder && !bookingTerminalStatus ? (
          <Alert
            type='info'
            showIcon
            message='QuickOnline order number is still pending. Subject copy stays disabled until the number is available.'
            style={{ marginBottom: 12 }}
          />
        ) : null}

        <Divider style={{ margin: '8px 0 16px' }} />

        <div className='quickstat-completed__section--lg'>
          <Flex
            justify='space-between'
            align='center'
            style={{ marginBottom: 8 }}
          >
            <Text strong>Subject</Text>
            <CustomButton
              text='Copy subject'
              icon={shipmentDrawerCopyIcon}
              iconPosition='left'
              onClick={handleCopySubject}
              disabled={!portalOrder}
              {...compactCopyButtonProps}
            />
          </Flex>
          <Input
            readOnly
            value={subject}
            variant='borderless'
            style={{ width: '100%' }}
            styles={{
              root: {
                backgroundColor: 'var(--light-gray)',
                borderRadius: 8,
                minHeight: 36,
                paddingInline: 11,
                border: 'none',
              },
              input: { backgroundColor: 'transparent' },
            }}
          />
        </div>

        <div className='quickstat-completed__section--xl'>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>
            Template
          </Text>
          <Select<QuickstatEmailTemplateId>
            className='custom-optgroup-label'
            placeholder='Select...'
            getPopupContainer={(trigger) =>
              trigger.closest('.ant-modal-content') ?? document.body
            }
            popupMatchSelectWidth
            styles={{
              popup: {
                root: { zIndex: 1250 },
              },
            }}
            style={{
              border: 0,
              backgroundColor: 'var(--light-gray)',
              minHeight: 36,
              borderRadius: 8,
              width: '100%',
            }}
            value={templateId}
            onChange={setTemplateId}
            options={QUICKSTAT_EMAIL_TEMPLATE_OPTIONS.map((o) => ({
              value: o.id,
              label: o.label,
            }))}
          />
        </div>

        <div className='quickstat-completed__section--sm'>
          <Flex
            justify='space-between'
            align='center'
            style={{ marginBottom: 8 }}
          >
            <Text strong>Email body</Text>
            <CustomButton
              text='Copy body'
              icon={shipmentDrawerCopyIcon}
              iconPosition='left'
              onClick={handleCopyBody}
              {...compactCopyButtonProps}
            />
          </Flex>
          {/* HTML preview (+ same markup on Paste into Outlook via clipboard `text/html`) */}
          <div
            className='onetime-text-area quickstat-booking-email-body-preview'
            dangerouslySetInnerHTML={{ __html: bodyHtml }}
          />
        </div>

        {waitingForOrderNumber &&
        !portalOrder &&
        !pollTimedOut &&
        !bookingTerminalStatus &&
        !submittedWithoutPortalOrder ? (
          <Text type='secondary' style={{ display: 'block', marginBottom: 8 }}>
            Retrieving QuickOnline order number… (up to a few minutes)
          </Text>
        ) : null}

        <Flex justify='flex-end' style={{ marginTop: 20 }}>
          <CustomButton
            text='Done'
            bgColor='#000'
            color='#fff'
            fontSize='14px'
            fontWeight='600'
            height='36px'
            borderRadius='8px'
            padding='0 32px'
            onClick={onClose}
          />
        </Flex>
      </Flex>
    </CustomModalWithoutButton>
  );
};

export default QuickstatBookingCompletedModal;
